import React, { ReactNode, useEffect, useState } from "react";
import NavBar from "./NavBar/NavBar";
import Footer from "./Footer/Footer";
import FooterBlockType from "../types/footerBlock.type";
import NavbarType from "../types/Navbar.type";
import classNames from "classnames";
import { Link } from "gatsby";
import Drawer from "./common/Drawer";
import PageRedirectionHandler from "../helper/handler/PageRedirection.handler";
import ShortcutMenu from "./common/ShortcutMenu";
import arrow from "../images/nav/white-arrow.svg";
import { getLocation } from "../util/location";;
import ContactModal from "./common/ContactModal";
import useScrollModal from "../hooks/useScrollModal";

interface LayoutProps {
  navbarData: NavbarType;
  footerData: FooterBlockType;
  children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({
  children,
  navbarData,
  footerData,
}: any) => {
  const [open, setOpen] = useState<boolean>(false);
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>(
    {}
  );

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [open]);

  const [country, setCountry] = useState<string | null>(null);

  useEffect(() => {
    const fetchLocation = async () => {
      const locationData = await getLocation();
      if (locationData?.country) {
        setCountry(locationData.country);
      }
    };
    fetchLocation();
  }, []);

  const activePageHandler = (pageUrl: string) => {
    return PageRedirectionHandler(pageUrl);
  };

  const groupedNavItems = () => {
    if (!navbarData?.pagesData || !Array.isArray(navbarData.pagesData))
      return [];

    const parentsMap: Record<string, any> = {};
    const childPages: any[] = [];

    navbarData.pagesData.forEach((page: any) => {
      if (page.parent) {
        childPages.push(page);
      } else {
        parentsMap[page.url] = { ...page, children: [] };
      }
    });

    childPages.forEach((child) => {
      const parentUrl = child.parent;
      const matchedParent = Object.keys(parentsMap).find(
        (url) => url === parentUrl
      );

      if (matchedParent) {
        parentsMap[matchedParent].children.push(child);
      } else {
        if (!parentsMap[parentUrl]) {
          parentsMap[parentUrl] = {
            name:
              parentUrl.replace(/\//g, "").replace(/-/g, " ").trim() || "Other",
            url: "#",
            children: [],
            isSynthetiParent: true,
          };
        }
        parentsMap[parentUrl].children.push(child);

        // 🆕 Set order from first child's order (if not already set)
        if (!parentsMap[parentUrl].order) {
          parentsMap[parentUrl].order = child.order ?? 999;
        }
      }
    });

    const navItems = Object.values(parentsMap).sort(
      (a: any, b: any) => (a.order ?? 999) - (b.order ?? 999)
    );

    return navItems;
  };

  const navItems = groupedNavItems();

  const toggleDropdown = (navItemUrl: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenDropdowns((prev) => {
      const currentState = prev[navItemUrl];
      return {
        ...prev,
        [navItemUrl]: !currentState,
      };
    });
  };

  // Initialize dropdown states for items with children
  useEffect(() => {
    const initialDropdownStates: Record<string, boolean> = {};
    navItems.forEach((item: any) => {
      if (item.children && item.children.length > 0) {
        // Initialize all dropdowns as open
        initialDropdownStates[item.url] = true;
      }
    });
    setOpenDropdowns(initialDropdownStates);
  }, []);

  const { isVisible, closeModal,title, subtitle } = useScrollModal({
    desktopThreshold: 70, // 70% scroll for desktop
    mobileThreshold: 50, // 50% scroll for mobile
  });


  return (
    <>
      <NavBar navbarData={navbarData} open={open} setOpen={setOpen} />
        <ContactModal isOpen={isVisible} onClose={closeModal} title={title} subtitle={subtitle}  />
      <Drawer isOpen={open} setIsOpen={setOpen}>
        <div className="px-[5%] py-2" style={{ marginTop: "100px" }}>
          <ul
            className={
              "align-center flex w-full flex-col justify-between text-start"
            }
          >
            {navItems.map((navItem: any, index: number) => {
              const activePage = activePageHandler(navItem?.url);
              const hasChildren =
                navItem.children && navItem.children.length > 0;
              const isDropdownOpen = openDropdowns[navItem.url];

              // Check if any child is active
              const isAnyChildActive =
                hasChildren &&
                navItem.children.some((child: any) =>
                  activePageHandler(child.url)
                );

              return (
                <React.Fragment key={index}>
                  <li
                    className={classNames(
                      "w-full self-center p-4 pl-6 font-medium lg:bg-hero-gradient-start"
                    )}
                    style={{
                      backgroundColor:
                        typeof window !== "undefined" &&
                        (activePage || isAnyChildActive)
                          ? "#065446"
                          : "",
                      borderBottom:
                        typeof window !== "undefined" &&
                        (activePage || isAnyChildActive)
                          ? ""
                          : "0.5px solid rgba(103, 237, 213, 0.64)",
                    }}
                  >
                    {hasChildren ? (
                      <button
                        onClick={(e) => toggleDropdown(navItem.url, e)}
                        className="text-white"
                      >
                        <span>{navItem.name?.toUpperCase()}</span>
                        <img
                          src={arrow}
                          alt="arrow"
                          className={classNames(
                            "ml-1 inline-block h-4 w-4 transform self-center transition-transform duration-300",
                            {
                              "rotate-180": isDropdownOpen,
                            }
                          )}
                        />
                      </button>
                    ) : (
                      <Link to={navItem.url} className="text-white">
                        {navItem.name?.toUpperCase()}
                      </Link>
                    )}
                  </li>

                  {/* Dropdown content for items with children */}
                  {hasChildren && isDropdownOpen && (
                    <div className="mt-2 flex flex-col">
                      {navItem.children.map((childItem: any, idx: number) => {
                        const isActiveChild = activePageHandler(childItem.url);
                        const icon =
                          childItem?.sidebarIcon?.media?.data?.[0]?.attributes
                            ?.url;

                        return (
                          <Link
                            key={idx}
                            to={childItem.url}
                            className={classNames(
                              "flex items-center gap-4 whitespace-nowrap px-8 py-2 text-[16px] text-sm font-normal text-white",
                              {
                                "font-normal text-white": isActiveChild,
                              }
                            )}
                          >
                            {icon && (
                              <img
                                src={icon}
                                alt={childItem.name}
                                className="h-8 w-8"
                              />
                            )}
                            {childItem.name}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </React.Fragment>
              );
            })}

            {/* Static Contact Us */}
            <li
              className={classNames(
                "w-full self-center p-4 pl-6 font-medium lg:bg-hero-gradient-start"
              )}
              style={{
                backgroundColor:
                  typeof window !== "undefined" &&
                  window.location.pathname === ""
                    ? "#065446"
                    : "",
              }}
            >
              <Link
                to={
                  typeof window !== "undefined" &&
                  window.location.pathname === "/"
                    ? "#contact-us"
                    : "/#contact-us"
                }
                className={"text-white"}
                onClick={() => {
                  if (
                    typeof window !== "undefined" &&
                    window.location.pathname === "/"
                  ) {
                    setOpen(false);
                  }
                }}
              >
                {navbarData?.buttonData?.text?.toUpperCase()}
              </Link>
            </li>
          </ul>
        </div>
      </Drawer>
      <main>
        {React.Children.map(children, (child) =>
          React.isValidElement<{ country?: string }>(child)
            ? React.cloneElement(child, { country: country ?? undefined })
            : child
        )}
      </main>
      {/* Contact Modal that shows on scroll */}
    
      {footerData && <Footer data={footerData?.data} meta={footerData?.meta} />}
      <ShortcutMenu />
    </>
  );
};

export default Layout;
